import React, { useRef, useState } from "react";
import AlertMessage from "../../../AlertMessage/AlertMessage";
import ConfirmPopup from "../../../ConfirmPopup/ConfirmPopup";
import { UpdateInvoiceStageAndArchiveStatusService } from "../../../../Services/InvoiceService/InvoiceService";

interface MarkAsReconciledButtonProps {
  invoiceId: number | null;
  onSuccess: () => void;
  onBeforeAction: (action: () => void) => void;
}

const MarkAsReconciledButton: React.FC<MarkAsReconciledButtonProps> = ({
  invoiceId,
  onSuccess,
  onBeforeAction,
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInProgress = useRef(false);

  const submitReconciliation = async () => {
    if (submissionInProgress.current) return;
    if (invoiceId === null) {
      AlertMessage({
        type: "error",
        text: "Something went wrong. The statement could not be reconciled. Please try again.",
      });
      return;
    }

    submissionInProgress.current = true;
    setIsSubmitting(true);
    try {
      const result = await UpdateInvoiceStageAndArchiveStatusService({
        invoiceIds: [invoiceId],
        stageId: null,
        archiveStatus: "Reconciled",
      });
      if (!result.success) {
        throw new Error(result.message);
      }

      AlertMessage({ type: "success", text: "Statement marked as reconciled." });
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error("Failed to reconcile supplier statement:", error);
      AlertMessage({
        type: "error",
        text: "Something went wrong. The statement could not be reconciled. Please try again.",
      });
    } finally {
      submissionInProgress.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="sv-action-btn sv-btn-reconciled"
        onClick={() => onBeforeAction(() => setOpen(true))}
      >
        <span className="sv-btn-text">Mark as reconciled</span>
      </button>

      <ConfirmPopup
        blur
        isOpen={open}
        title="Mark as reconciled"
        message={
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "6px" }}>1 supplier statement</div>
            <div style={{ color: "#555", fontSize: "13px", lineHeight: "1.6" }}>
              Are you sure you want to mark this statement as reconciled?
            </div>
          </div>
        }
        confirmLabel={isSubmitting ? "Processing..." : "Mark as reconciled"}
        cancelLabel="Cancel"
        confirmColor="var(--color-brand)"
        onConfirm={() => void submitReconciliation()}
        onCancel={() => setOpen(false)}
        confirmDisabled={isSubmitting}
      />
    </>
  );
};

export default MarkAsReconciledButton;
